/**
 * Reading a person's engagements as a history rather than as a list of rows.
 *
 * The People list has one narrow cell for everything a person's terms say, and it
 * used to print one chip per engagement. Somebody rehired twice therefore read as
 * "Ended Partner, Ended Partner, Active Partner" — three identical-looking chips with
 * no date on any of them, which is what a duplicate looks like even though it is
 * ordinary history. The store cannot help: `attachChildren` loads every engagement
 * for the page filtered by population only, so the cell always receives the whole
 * history and the question is what to do with it.
 *
 * What these functions answer is: per population, which single term stands for the
 * person right now, how many terms sit behind it, and which days that term covered.
 * A person's own page is where the terms are read one by one, and the row is a link
 * into it.
 */

/**
 * What summarising needs from a term.
 *
 * Structural rather than the generated `Engagement`, so the rules can be exercised on
 * plain objects and so a new field on the fragment does not reach in here. The caller
 * keeps its own richer type: every function below is generic in it and hands the same
 * object back, so the cell still renders from the real row.
 */
export type SummarisableTerm = {
  readonly population: string;
  readonly isCurrent: boolean;
  readonly startDate?: string | null;
  readonly effectiveEndDate?: string | null;
};

/** One population's history, collapsed to the term that stands for it. */
export type PopulationHistory<T> = {
  readonly population: string;
  /** The term on show: the current one, or the most recent of the closed ones. */
  readonly term: T;
  /** How many further terms this person holds in that population. */
  readonly earlier: number;
};

/**
 * Which term stands for a population, given every term in it.
 *
 * The current term wins outright, and there can only be one: the partial unique index
 * `engagement_one_current_per_identity_population` refuses a second term with no
 * termination. That ordering is what keeps the "not closed out" flag visible — a
 * lapsed term is by definition one nobody terminated (`HasLapsed` returns false unless
 * `IsCurrent`), so it is always the term picked here and can never be collapsed away
 * behind an older one.
 *
 * With no current term the most recently started one stands for the history, matching
 * what "the last thing that happened" means to a reader. A term with no start date
 * sorts last rather than first: an undated term that somebody has already ended is a
 * withdrawn offer, not the person's latest position.
 */
function representative<T extends SummarisableTerm>(terms: readonly T[]): T {
  let best = terms[0] as T;
  for (const term of terms.slice(1)) {
    if (best.isCurrent) {
      break;
    }
    if (term.isCurrent) {
      best = term;
      continue;
    }
    const a = best.startDate ?? "";
    const b = term.startDate ?? "";
    // ISO dates, so string order is date order. "" is the unset case and loses to
    // every real date, which is the "sorts last" rule above.
    if (b > a) {
      best = term;
    }
  }
  return best;
}

/**
 * A person's engagements, one entry per population.
 *
 * Populations come back in the order their first term appears, which is the order the
 * store already sorted them into, so the cell does not reshuffle between renders.
 */
export function summariseEngagements<T extends SummarisableTerm>(
  terms: readonly T[]
): PopulationHistory<T>[] {
  const byPopulation = new Map<string, T[]>();
  for (const term of terms) {
    const held = byPopulation.get(term.population);
    if (held) {
      held.push(term);
    } else {
      byPopulation.set(term.population, [term]);
    }
  }
  return [...byPopulation].map(([population, held]) => ({
    population,
    term: representative(held),
    earlier: held.length - 1
  }));
}

/**
 * The days a term covered, as one line.
 *
 * `effectiveEndDate` and not `endDate`, because the question is which days the term
 * ran: a term cut short keeps the contracted end it was agreed for, and printing that
 * date next to an "Ended" badge would claim the person was still engaged for months
 * after they left. The server derives it from the termination and the contracted end
 * together, so this line cannot disagree with the badge beside it.
 *
 * Null for a term carrying neither date: there is no recorded period to show.
 * Its status still comes from the server; an unterminated, undated term is active.
 */
export function describeTermPeriod(term: SummarisableTerm): string | null {
  const from = term.startDate ?? null;
  const to = term.effectiveEndDate ?? null;
  if (from && to) {
    // An end before the start is the withdrawn-offer record: somebody ended the term
    // before its first day arrived, which migration 000016 went out of its way to keep
    // representable. The contracted end cannot produce it — `engagement_dates_ordered`
    // holds end_date >= start_date — so this is always a termination. The term covered
    // no days at all, and a range would print them backwards as though it had.
    return to < from ? `Ended ${to}, before it began` : `${from} – ${to}`;
  }
  if (from) {
    return `From ${from}`;
  }
  if (to) {
    // No start date and an end: an offer withdrawn before a first day was ever agreed,
    // or a term whose start was never recorded. "Until" rather than a range with a gap.
    return `Until ${to}`;
  }
  return null;
}

/**
 * The caption under a population's badge: when the term on show ran, and how much
 * history sits behind it.
 *
 * The count is the whole point of the cell. Three chips reading "Ended Partner, Ended
 * Partner, Active Partner" are indistinguishable from duplicated data; one badge and
 * "2 earlier terms" says the same thing as a history, and says where the rest of it
 * is — the row opens the person, whose own page lists them.
 */
export function describeEngagementHistory(
  entry: PopulationHistory<SummarisableTerm>
): string | null {
  const parts = [describeTermPeriod(entry.term)];
  if (entry.earlier > 0) {
    parts.push(`${entry.earlier} earlier ${entry.earlier === 1 ? "term" : "terms"}`);
  }
  const caption = parts.filter((part): part is string => part !== null);
  return caption.length > 0 ? caption.join(" · ") : null;
}
